/**
 * Vue 认证 Composable
 * 提供响应式的认证状态和方法
 */

import { ref, computed, readonly, type Ref, type ComputedRef } from 'vue'
import type { AuthService } from '../../../core/services/AuthService'
import type {
  UserInfo,
  LoginParams,
  QRCodeInfo,
  SendVerifyCodeResponse,
  SlideCaptchaInfo,
  CreateSsoCodeParams,
  SsoCodeResponse
} from '../../../types/auth'

export interface UseAuthReturn {
  // 状态
  isLogin: ComputedRef<boolean>
  userInfo: ComputedRef<UserInfo | null>
  token: ComputedRef<string | null>
  qrCodeInfo: Ref<QRCodeInfo | null>

  // 加载状态
  loading: Ref<boolean>
  error: Ref<Error | null>

  // 验证码倒计时
  verifyCodeCountdown: Ref<number>

  // 方法
  startQRLogin: (onStatusChange?: (status: string) => void) => Promise<void>
  stopQRLogin: () => void
  sendVerifyCode: (phone: string) => Promise<void>
  phoneLogin: (params: LoginParams) => Promise<void>
  logout: () => Promise<void>
  refreshToken: () => Promise<void>
  getImageCaptcha: () => Promise<any>
  getSlideCaptcha: (params?: {
    phone?: string
    smsType?: 'login' | 'bind' | 'reset'
  }) => Promise<SlideCaptchaInfo>
  sendVerifyCodeWithCaptcha: (params: {
    phone: string
    captcha_key: string
    captcha_code: string
    smsType: 'login' | 'bind' | 'reset'
  }) => Promise<SendVerifyCodeResponse>
  sendVerifyCodeWithSlide: (params: {
    phone: string
    captcha_key: string
    slide_x: number
    slide_y: number
    smsType: 'login' | 'bind' | 'reset'
  }) => Promise<SendVerifyCodeResponse>
  wechatLoginRedirect: (callbackUrl: string) => Promise<void>
  createSsoCode: (params: CreateSsoCodeParams) => Promise<SsoCodeResponse>
  createSsoCodeAndRedirect: (params: CreateSsoCodeParams) => Promise<SsoCodeResponse>
  getMiniProgramLoginQrcode: (options?: {
    env_version?: 'release' | 'trial' | 'devop'
    login_params?: string
  }) => Promise<{ base64_img: string; uuid: string }>
  checkMiniProgramLoginStatus: (
    login_uuid: string,
    options?: { gapTime?: number; maxTime?: number }
  ) => Promise<{ token: string }>
  stopMiniProgramQRLogin: () => void
  getMiniProgramLoginParamsByUUID: (login_uuid: string) => Promise<any>
  bindMiniProgramTokenByUUID: (login_uuid: string) => Promise<void>
  miniProgramLoginLegacy: (params: { wxCode: string; login_uuid?: string }) => Promise<any>
}

/**
 * 创建认证 Composable
 */
export function createUseAuth(authService: AuthService): () => UseAuthReturn {
  return function useAuth(): UseAuthReturn {
    // 响应式状态
    const loading = ref(false)
    const error = ref<Error | null>(null)
    const qrCodeInfo = ref<QRCodeInfo | null>(null)
    const verifyCodeCountdown = ref(0)

    // 从 AuthStore 获取计算属性（直接使用 store 的响应式引用）
    const authStore = authService.getAuthStore()

    // 使用 computed 包装 authStore 的值
    const userInfo = computed(() => authStore.userInfo)
    const token = computed(() => authStore.token)

    // 启动验证码倒计时更新
    let countdownTimer: any = null
    const startCountdownUpdate = () => {
      if (countdownTimer) {
        clearInterval(countdownTimer)
      }
      countdownTimer = setInterval(() => {
        verifyCodeCountdown.value = authService.getVerifyCodeCountdown()
        if (verifyCodeCountdown.value <= 0 && countdownTimer) {
          clearInterval(countdownTimer)
          countdownTimer = null
        }
      }, 1000)
    }

    /**
     * 开始二维码登录
     */
    const startQRLogin = async (onStatusChange?: (status: string) => void) => {
      try {
        loading.value = true
        error.value = null

        const info = await authService.startQRLogin(onStatusChange)
        qrCodeInfo.value = info
      } catch (e) {
        error.value = e as Error
        throw e
      } finally {
        loading.value = false
      }
    }

    /**
     * 停止二维码登录
     */
    const stopQRLogin = () => {
      authService.stopQRLogin()
      qrCodeInfo.value = null
    }

    /**
     * 发送验证码
     */
    const sendVerifyCode = async (phone: string) => {
      try {
        loading.value = true
        error.value = null

        await authService.sendVerifyCode(phone)

        // 启动倒计时更新
        startCountdownUpdate()
      } catch (e) {
        error.value = e as Error
        throw e
      } finally {
        loading.value = false
      }
    }

    /**
     * 手机号登录
     */
    const phoneLogin = async (params: LoginParams) => {
      try {
        loading.value = true
        error.value = null

        await authService.phoneLogin(params)
      } catch (e) {
        error.value = e as Error
        throw e
      } finally {
        loading.value = false
      }
    }

    /**
     * 登出
     */
    const logout = async () => {
      try {
        loading.value = true
        error.value = null

        await authService.logout()

        // 清理倒计时
        if (countdownTimer) {
          clearInterval(countdownTimer)
          countdownTimer = null
        }
        verifyCodeCountdown.value = 0
      } catch (e) {
        error.value = e as Error
        throw e
      } finally {
        loading.value = false
      }
    }

    /**
     * 刷新 Token
     */
    const refreshToken = async () => {
      try {
        loading.value = true
        error.value = null

        await authService.refreshToken()
      } catch (e) {
        error.value = e as Error
        throw e
      } finally {
        loading.value = false
      }
    }

    /**
     * 获取图片验证码
     */
    const getImageCaptcha = async () => {
      try {
        loading.value = true
        error.value = null

        return await authService.getImageCaptcha()
      } catch (e) {
        error.value = e as Error
        throw e
      } finally {
        loading.value = false
      }
    }

    /**
     * 获取滑块验证码
     */
    const getSlideCaptcha = async (params?: {
      phone?: string
      smsType?: 'login' | 'bind' | 'reset'
    }) => {
      try {
        loading.value = true
        error.value = null

        return await authService.getSlideCaptcha(params)
      } catch (e) {
        error.value = e as Error
        throw e
      } finally {
        loading.value = false
      }
    }

    /**
     * 发送验证码（带图片验证码）
     */
    const sendVerifyCodeWithCaptcha = async (params: {
      phone: string
      captcha_key: string
      captcha_code: string
      smsType: 'login' | 'bind' | 'reset'
    }) => {
      try {
        loading.value = true
        error.value = null

        const result = await authService.sendVerifyCodeWithCaptcha(params)

        // 检查业务错误码，参考激活码兑换的模式
        if (result.code === 0 || result.code === 200) {
          // 启动倒计时更新
          startCountdownUpdate()
        }

        return result
      } catch (e) {
        error.value = e as Error
        throw e
      } finally {
        loading.value = false
      }
    }

    /**
     * 发送验证码（带滑块验证）
     */
    const sendVerifyCodeWithSlide = async (params: {
      phone: string
      captcha_key: string
      slide_x: number
      slide_y: number
      smsType: 'login' | 'bind' | 'reset'
    }) => {
      try {
        loading.value = true
        error.value = null

        const result = await authService.sendVerifyCodeWithSlide(params)
        if (result.code === 0 || result.code === 200) {
          startCountdownUpdate()
        }
        return result
      } catch (e) {
        error.value = e as Error
        throw e
      } finally {
        loading.value = false
      }
    }

    /**
     * 微信登录（跳转授权，兼容旧版）
     */
    const wechatLoginRedirect = async (callbackUrl: string) => {
      try {
        loading.value = true
        error.value = null
        await authService.wechatLoginRedirect(callbackUrl)
      } catch (e) {
        error.value = e as Error
        throw e
      } finally {
        loading.value = false
      }
    }

    /** 创建 SSO code */
    const createSsoCode = async (params: CreateSsoCodeParams) => {
      try {
        loading.value = true
        error.value = null
        return await authService.createSsoCode(params)
      } catch (e) {
        error.value = e as Error
        throw e
      } finally {
        loading.value = false
      }
    }

    /** 创建 SSO code 并跳转 */
    const createSsoCodeAndRedirect = async (params: CreateSsoCodeParams) => {
      try {
        loading.value = true
        error.value = null
        return await authService.createSsoCodeAndRedirect(params)
      } catch (e) {
        error.value = e as Error
        throw e
      } finally {
        loading.value = false
      }
    }

    /**
     * 小程序登录二维码
     */
    const getMiniProgramLoginQrcode = async (options?: {
      env_version?: 'release' | 'trial' | 'devop'
      login_params?: string
    }) => {
      try {
        loading.value = true
        error.value = null
        return await authService.getMiniProgramLoginQrcode(options)
      } catch (e) {
        error.value = e as Error
        throw e
      } finally {
        loading.value = false
      }
    }

    /**
     * 小程序扫码登录状态
     */
    const checkMiniProgramLoginStatus = async (
      login_uuid: string,
      options?: { gapTime?: number; maxTime?: number }
    ) => {
      try {
        loading.value = true
        error.value = null
        return await authService.checkMiniProgramLoginStatus(login_uuid, options)
      } catch (e) {
        error.value = e as Error
        throw e
      } finally {
        loading.value = false
      }
    }

    /**
     * 停止小程序扫码轮询
     */
    const stopMiniProgramQRLogin = () => {
      authService.stopMiniProgramQRLogin()
    }

    /**
     * 获取小程序登录参数
     */
    const getMiniProgramLoginParamsByUUID = async (login_uuid: string) => {
      try {
        loading.value = true
        error.value = null
        return await authService.getMiniProgramLoginParamsByUUID(login_uuid)
      } catch (e) {
        error.value = e as Error
        throw e
      } finally {
        loading.value = false
      }
    }

    /**
     * 绑定小程序登录 Token
     */
    const bindMiniProgramTokenByUUID = async (login_uuid: string) => {
      try {
        loading.value = true
        error.value = null
        await authService.bindMiniProgramTokenByUUID(login_uuid)
      } catch (e) {
        error.value = e as Error
        throw e
      } finally {
        loading.value = false
      }
    }

    /**
     * 小程序登录（旧版接口）
     */
    const miniProgramLoginLegacy = async (params: { wxCode: string; login_uuid?: string }) => {
      try {
        loading.value = true
        error.value = null
        return await authService.miniProgramLoginLegacy(params)
      } catch (e) {
        error.value = e as Error
        throw e
      } finally {
        loading.value = false
      }
    }

    return {
      // 状态（用 computed 包装确保类型一致性）
      isLogin: computed(() => authStore.isLogin.value),
      userInfo: readonly(userInfo) as ComputedRef<UserInfo | null>,
      token: readonly(token) as ComputedRef<string | null>,
      qrCodeInfo: readonly(qrCodeInfo) as Ref<QRCodeInfo | null>,

      // 加载状态
      loading: readonly(loading) as Ref<boolean>,
      error: readonly(error) as Ref<Error | null>,

      // 验证码倒计时
      verifyCodeCountdown: readonly(verifyCodeCountdown) as Ref<number>,

      // 方法
      startQRLogin,
      stopQRLogin,
      sendVerifyCode,
      phoneLogin,
      logout,
      refreshToken,
      getImageCaptcha,
      getSlideCaptcha,
      sendVerifyCodeWithCaptcha,
      sendVerifyCodeWithSlide,
      wechatLoginRedirect,
      createSsoCode,
      createSsoCodeAndRedirect,
      getMiniProgramLoginQrcode,
      checkMiniProgramLoginStatus,
      stopMiniProgramQRLogin,
      getMiniProgramLoginParamsByUUID,
      bindMiniProgramTokenByUUID,
      miniProgramLoginLegacy
    }
  }
}
